import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, MessageCircle, ArrowUp, Sparkles, ImagePlus, X, Search, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatRelative } from "@/lib/relative";

export default function Forum() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const region = searchParams.get("region") || "all";
  const sort = searchParams.get("sort") || "recent";
  const qParam = searchParams.get("q") || "";
  const [query, setQuery] = useState(qParam);
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => { api.get("/regions").then((r) => setRegions(r.data)).catch(() => {}); }, []);

  // Debounce search input -> URL
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (query.trim()) next.set("q", query.trim()); else next.delete("q");
      setSearchParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (region && region !== "all") params.region = region;
    if (sort) params.sort = sort;
    if (qParam) params.q = qParam;
    api.get("/forum/threads", { params })
      .then((r) => setThreads(r.data))
      .catch(() => toast.error("Kunde inte ladda forumet"))
      .finally(() => setLoading(false));
  }, [region, sort, qParam]);

  const regionMap = useMemo(() => Object.fromEntries(regions.map((r) => [r.id, r.name])), [regions]);

  const updateParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v && v !== "all" && v !== "recent") next.set(k, v); else next.delete(k);
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="forum-page">
      <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
        <span className="title-rule" />Community
      </div>
      <div className="flex items-end justify-between mb-7">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight">Forum</h1>
          <p className="text-sm text-muted-foreground mt-2">Ställ frågor, dela kunskap, lär av varandra.</p>
        </div>
        {user ? (
          <Button onClick={() => setComposerOpen(true)} data-testid="new-thread-btn">
            <Plus className="size-4 mr-2" />Ny fråga
          </Button>
        ) : (
          <Button onClick={() => nav("/logga-in", { state: { from: "/forum" } })} data-testid="login-to-post">
            Logga in för att posta
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Sök i forumet…"
            className="pl-9"
            data-testid="forum-search"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Rensa sökning"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <Select value={region} onValueChange={(v) => updateParam("region", v)}>
          <SelectTrigger className="w-[220px]" data-testid="forum-region"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla regioner</SelectItem>
            {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
          <SelectTrigger className="w-[180px]" data-testid="forum-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Senast aktiva</SelectItem>
            <SelectItem value="top">Mest uppröstade</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {user && !user.email_verified && (
        <Card className="p-4 mb-5 bg-card border-accent/40" data-testid="verify-banner">
          <div className="flex items-start gap-3 text-sm">
            <AlertCircle className="size-5 text-accent shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Verifiera din e-post för att posta</p>
              <p className="text-muted-foreground text-xs mt-0.5">
                Vi har skickat ett verifieringsmail till <strong>{user.email}</strong>. Kolla skräpkorgen om du inte ser det.
                Du kan läsa forumet utan att verifiera, men inte posta.
              </p>
            </div>
            <Link to="/profil" className="text-xs text-primary hover:text-accent font-mono uppercase tracking-widest shrink-0">
              Profil →
            </Link>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-muted-foreground">Laddar…</div>
      ) : threads.length === 0 ? (
        <Card className="p-10 text-center bg-card border-dashed">
          <Sparkles className="size-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="font-serif text-lg mb-1">Inga trådar än</p>
          <p className="text-sm text-muted-foreground">Var först med att ställa en fråga!</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="thread-list">
          {threads.map((t) => (
            <Link
              key={t.thread_id}
              to={`/forum/${t.thread_id}`}
              data-testid={`thread-${t.thread_id}`}
              className="block"
            >
              <Card className="p-4 hover:border-accent/60 transition-colors bg-card">
                <div className="flex items-start gap-3">
                  <div className="text-center min-w-[44px] py-1">
                    <div className="font-serif text-lg leading-none">{t.votes}</div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-1 uppercase">röster</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg font-medium mb-1 leading-snug line-clamp-2">{t.title}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-2">{t.body}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-muted-foreground">
                      <span>{t.author?.name || "Anonym"}</span>
                      {t.author?.profession && <span className="text-muted-foreground/70">· {t.author.profession}</span>}
                      <span>·</span>
                      <span>{formatRelative(t.created_at)}</span>
                      {t.region && regionMap[t.region] && (
                        <Badge variant="secondary" className="font-mono text-[10px]">{regionMap[t.region]}</Badge>
                      )}
                      {t.best_reply_id && (
                        <Badge variant="outline" className="text-[10px] border-primary/50 text-primary">Löst</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground font-mono text-xs shrink-0">
                    <MessageCircle className="size-3.5" /> {t.reply_count}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {user && (
        <ComposerDialog open={composerOpen} onOpenChange={setComposerOpen} regions={regions} onCreated={(t) => nav(`/forum/${t.thread_id}`)} />
      )}
    </div>
  );
}

function ComposerDialog({ open, onOpenChange, regions, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [region, setRegion] = useState("all");
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads/image", fd);
      setImgUrl(r.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bilduppladdning misslyckades");
    }
  };

  const submit = async () => {
    if (title.trim().length < 4 || body.trim().length < 4) {
      toast.warning("Titel och fråga måste vara minst 4 tecken.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/forum/threads", {
        title: title.trim(),
        body: body.trim(),
        region: region === "all" ? null : region,
        image_url: imgUrl,
      });
      toast.success("Tråden skapad!");
      onOpenChange(false);
      setTitle(""); setBody(""); setRegion("all"); setImgUrl(null);
      onCreated(r.data);
    } catch (e) {
      toast.error("Kunde inte skapa tråden");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-card" data-testid="composer-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Ny fråga</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 block">Titel</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} data-testid="composer-title" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 block">Region (valfritt)</label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="composer-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Ingen specifik region</SelectItem>
                {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 block">Fråga (markdown stöds)</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="resize-none" data-testid="composer-body" />
          </div>
          {imgUrl ? (
            <div className="relative inline-block">
              <img src={imgUrl} alt="bilaga" className="max-h-32 rounded border border-border" />
              <button onClick={() => setImgUrl(null)} className="absolute -top-2 -right-2 size-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"><X className="size-3" /></button>
            </div>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="composer-img-btn">
              <ImagePlus className="size-4 mr-2" />Bifoga bild
            </Button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <Button onClick={submit} disabled={loading} className="w-full" data-testid="composer-submit">
            {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : <Plus className="size-4 mr-2" />}Publicera
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
